import type { ProfileTeamData } from "@qendo/profile";
import type { LeaveTeamPayload } from "@qendo/profile/team";
import type { ListData } from "@qendo/types";
import type { DeleteAccountPayload, UserData } from "@qendo/user";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AxiosResponse } from "axios";
import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from "react";
import { ProfileTeam } from "~/entities/profile/profile-team";
import { User } from "~/entities/user";
import { type ApiError, useApi } from "~/providers/ApiProvider";
import { useSession } from "~/providers/SessionProvider";
import { filled } from "~/util";
import { mergeQuery, removeQuery } from "~/util/api";
import { filterUnique } from "~/util/array";
import { UserKey, userStorage } from "~/util/secure-storage";

declare interface UserState {
    user: User;
    teams: ProfileTeam[];
    primary_profile: string | null;
    team_ids: string[];
    active: boolean;
    refreshUser(): Promise<void>;
    refreshTeams(): Promise<void>;
    isPrimaryTeam(profile_id: string | null | undefined): boolean;
    hasTeam(profile_id: string | null | undefined): boolean;
    acceptInvite(token: string): Promise<string>;
    leaveTeam(payload: LeaveTeamPayload): Promise<void>;
    deleteAccount(payload: DeleteAccountPayload): Promise<void>;
}

const EmptyUser = User.from({ id: "", name: "Anonymous", email: "" });

export const UserContext = createContext<UserState>({
    user: EmptyUser,
    teams: [],
    team_ids: [],
    primary_profile: null,
    active: false,
    refreshUser: async () => {
        return;
    },
    refreshTeams: async () => {
        return;
    },
    isPrimaryTeam: (_profile_id) => false,
    hasTeam: (_profile_id) => false,
    acceptInvite: async (_token) => "",
    leaveTeam: async (_payload) => {
        return;
    },
    deleteAccount: async (_payload) => {
        return;
    },
});

export function useUser() {
    const value = useContext(UserContext);
    if (!value) {
        throw new Error("useUser must be wrapped in a <UserProvider />");
    }
    return value;
}

export function UserProvider({ children }: { children: ReactNode }) {
    const queryClient = useQueryClient();
    const { client } = useApi();
    const { active, is_share_user, logOut } = useSession();
    const [hydrated, setHydrated] = useState(false);
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(false);
    const [loaded, setLoaded] = useState(false);

    const queryKey = ["teams", user?.id ?? null];
    const teamsQuery = useQuery({
        queryKey,
        queryFn: async () => {
            if (user?.id) {
                return await client()
                    .get("teams")
                    .then(({ data }: AxiosResponse<ListData<ProfileTeamData>>) => {
                        return data.data;
                    });
            }

            return [];
        },
        placeholderData: [],
        staleTime: 1000 * 60 * 60, // 1 hour
        refetchOnMount: true,
    });

    const joinTeamMutation = useMutation({
        mutationFn: async (token: string) => {
            return await client()
                .patch(`/invitation/${token}`)
                .then(({ data }: AxiosResponse<ProfileTeamData>) => {
                    return data;
                });
        },
        onSuccess: (team: ProfileTeamData) => {
            queryClient.setQueryData(queryKey, mergeQuery(team));
        },
    });
    const acceptInvite = async (token: string) => {
        return await joinTeamMutation.mutateAsync(token).then(({ profile_id }) => profile_id);
    };

    const destroyTeamMutation = useMutation({
        mutationFn: async ({ id, ...payload }: LeaveTeamPayload) => {
            const url = `teams/${id}`;

            return await client()
                .patch(url, payload)
                .then(() => id)
                .catch((error: ApiError) => {
                    if (error.status === 404) {
                        return id;
                    }

                    throw error;
                });
        },
        onSuccess: (id: string) => {
            queryClient.setQueryData(queryKey, removeQuery(id));
        },
    });
    const leaveTeam = async (payload: LeaveTeamPayload) => {
        if (is_share_user) {
            return;
        }

        await destroyTeamMutation.mutateAsync(payload);
    };

    const teams = useMemo(() => (teamsQuery.data ?? []).map((t) => ProfileTeam.from(t)), [teamsQuery]);

    const team_ids = useMemo(
        () =>
            [user?.profile_id ?? "", ...teams.map((t: ProfileTeam) => t.profile_id)]
                .filter(filled)
                .filter(filterUnique),
        [user, teams],
    );

    const refreshUser = async () => {
        if (active) {
            if (loading) {
                return;
            }
            setLoading(true);

            await client()
                .get("user")
                .then(({ data }: AxiosResponse<UserData>) => {
                    setUser(User.from(data));
                })
                .finally(() => setLoading(false));
        } else {
            user && setUser(null);
        }
    };

    const refreshTeams = async () => {
        await queryClient.invalidateQueries({ queryKey, refetchType: "all" });
    };

    useEffect(() => {
        if (is_share_user) {
            setHydrated(true);
            setUser(null);
            setLoaded(false);
        } else {
            setUser(null);
            setLoaded(false);
            setHydrated(false);
        }
    }, [is_share_user]);

    // biome-ignore lint/correctness/useExhaustiveDependencies(refreshUser): API
    // biome-ignore lint/correctness/useExhaustiveDependencies(refreshTeams): API
    useEffect(() => {
        if (active && !hydrated) {
            Promise.allSettled([userStorage.getItem<User>(UserKey.user).then(setUser)]).then(() => {
                setHydrated(true);
            });
        } else if (hydrated && active && !loaded) {
            Promise.allSettled([refreshUser(), refreshTeams()]).then(() => {
                setLoaded(true);
            });
        } else if (hydrated && !active) {
            user && setUser(null);
            setHydrated(false);
            setLoaded(false);
        }
    }, [active, loaded, hydrated, user]);

    useEffect(() => {
        hydrated && !is_share_user && userStorage.setItem(UserKey.user, user);
    }, [user, is_share_user, hydrated]);

    const ready = useMemo(() => Boolean(active && hydrated && user), [active, hydrated, user]);

    const primary_profile = user?.profile_id ?? null;

    const isPrimaryTeam = (profile_id: string | null | undefined) =>
        Boolean(primary_profile && profile_id === primary_profile);

    const hasTeam = (profile_id: string | null | undefined) =>
        Boolean(profile_id && (isPrimaryTeam(profile_id) || team_ids.includes(profile_id)));

    const deleteAccount = async (payload: DeleteAccountPayload) => {
        return await client()
            .patch("user/destroy", payload)
            .then(() => logOut());
    };

    const value: UserState = {
        user: user ?? EmptyUser,
        teams,
        team_ids,
        active: ready,
        primary_profile,
        refreshUser,
        refreshTeams,
        isPrimaryTeam,
        hasTeam,
        acceptInvite,
        leaveTeam,
        deleteAccount,
    };

    return <UserContext.Provider value={value}>{hydrated && children}</UserContext.Provider>;
}
